import { useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../redux";
import { demoShareActions, DemoHighlight } from "../../redux/reducers/demoShareSlice";
import { agentSessionActions } from "../../redux/reducers/agentSessionSlice";
import { makeRequest } from "../../utils/request";
import { Dialog } from "../base/Dialog";
import { Button, InputField, Text, View, Badge, MenuField } from "../base";
import { Icon } from "../base/Icon";

type ShareDemoDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  agentId?: string;
};

const ACCENT_SWATCHES = [
  { name: "Teal", hex: "#14b8a6" },
  { name: "Indigo", hex: "#6366f1" },
  { name: "Emerald", hex: "#10b981" },
  { name: "Rose", hex: "#f43f5e" },
  { name: "Amber", hex: "#f59e0b" },
];

const MAX_HIGHLIGHTS = 4;

// Sensible starter stats — the admin edits the numbers or removes any before sharing.
const DEFAULT_HIGHLIGHTS: DemoHighlight[] = [
  { label: "Avg. response", value: "0.8s" },
  { label: "Resolution rate", value: "94%" },
  { label: "Availability", value: "24/7" },
];

type CreatedShare = { slug: string; title: string; password?: string };

/**
 * Admin "Share Demo" surface, opened from the agent header. Assembles a polished,
 * password-protected public demo page (`/demo/:slug`) for an agent — featured
 * recordings, marketing copy, stat highlights — and manages existing links.
 *
 * The page is always password-protected with a server-generated password that is
 * surfaced once on create (and via "New password" on existing links).
 */
export const ShareDemoDialog = ({ isOpen, onClose, agentId }: ShareDemoDialogProps) => {
  const dispatch = useAppDispatch();

  const shares = useAppSelector((state) => Object.values(state.demoShares.entities).filter((s) => s.agentId === agentId));
  const sessions = useAppSelector((state) =>
    Object.values(state.agentSession.entities)
      .filter((s) => s.agentId === agentId)
      .sort((a, b) => new Date(b.createdAt as any).getTime() - new Date(a.createdAt as any).getTime()),
  );

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [intro, setIntro] = useState("");
  const [accentColor, setAccentColor] = useState(ACCENT_SWATCHES[0].hex);
  const [ctas, setCtas] = useState<{ label: string; url: string }[]>([]);
  const [webCallEnabled, setWebCallEnabled] = useState(true);
  const [phoneCallEnabled, setPhoneCallEnabled] = useState(false);
  const [highlights, setHighlights] = useState<DemoHighlight[]>(() => DEFAULT_HIGHLIGHTS.map((h) => ({ ...h })));
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<CreatedShare | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [regen, setRegen] = useState<Record<string, string>>({});

  // Fetch this agent's shares + sessions whenever the dialog opens.
  useEffect(() => {
    if (!isOpen || !agentId) return;
    dispatch(demoShareActions.get({}));
    dispatch(agentSessionActions.get({ agentId, limit: "100" }));
  }, [isOpen, agentId, dispatch]);

  const publicUrlFor = (slug: string) => `${window.location.origin}/demo/${slug}`;

  const resetForm = () => {
    setTitle("");
    setSubtitle("");
    setIntro("");
    setAccentColor(ACCENT_SWATCHES[0].hex);
    setCtas([]);
    setWebCallEnabled(true);
    setPhoneCallEnabled(false);
    setHighlights(DEFAULT_HIGHLIGHTS.map((h) => ({ ...h })));
    setSelectedSessionIds([]);
    setError("");
  };

  const handleClose = () => {
    resetForm();
    setCreated(null);
    setRegen({});
    setCopiedKey(null);
    onClose();
  };

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((c) => (c === key ? null : c)), 2000);
    } catch {
      setError("Couldn't copy to clipboard");
    }
  };

  const toggleSession = (id: string) => {
    setSelectedSessionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const updateHighlight = (index: number, patch: Partial<DemoHighlight>) => {
    setHighlights((prev) => prev.map((h, i) => (i === index ? { ...h, ...patch } : h)));
  };

  const updateCta = (index: number, patch: Partial<{ label: string; url: string }>) => {
    setCtas((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const toggleRow = (label: string, hint: string, checked: boolean, onChange: (v: boolean) => void) => (
    <View className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
      <View className="min-w-0">
        <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">{label}</Text>
        {hint && (
          <Text
            variant="muted"
            className="text-xs block mt-0.5"
          >
            {hint}
          </Text>
        )}
      </View>
      <button
        type="button"
        aria-pressed={checked}
        onClick={() => onChange(!checked)}
        className={`shrink-0 mt-0.5 h-5 w-9 rounded-full transition-colors relative ${checked ? "bg-brand" : "bg-gray-300 dark:bg-gray-600"}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
      </button>
    </View>
  );

  const handleCreate = async () => {
    if (!agentId) return;
    if (!title.trim()) {
      setError("Give the demo a title");
      return;
    }

    setIsCreating(true);
    setError("");
    try {
      const cleanHighlights = highlights.filter((h) => h.label.trim() && h.value.trim());
      // Normalize CTA URLs — the backend requires a protocol, so prepend https://
      // if the admin typed a bare domain (e.g. "cal.com/team").
      const cleanCtas = ctas
        .filter((c) => c.label.trim() && c.url.trim())
        .map((c) => {
          const u = c.url.trim();
          return { label: c.label.trim(), url: /^https?:\/\//i.test(u) ? u : `https://${u}` };
        });

      const result = await dispatch(
        demoShareActions.create({
          agentId,
          title: title.trim(),
          subtitle: subtitle.trim() || undefined,
          intro: intro.trim() || undefined,
          accentColor: accentColor || undefined,
          ctas: cleanCtas.length ? cleanCtas : undefined,
          webCallEnabled,
          phoneCallEnabled,
          highlights: cleanHighlights.length ? cleanHighlights : undefined,
          featuredSessions: selectedSessionIds.map((sessionId) => ({ sessionId })),
        } as any),
      ).unwrap();

      setCreated({ slug: result.slug, title: result.title, password: result.generatedPassword });
      // Eagerly copy the share message so the admin can paste it right away.
      if (result.generatedPassword) {
        await copy("combo", `${publicUrlFor(result.slug)}\nPassword: ${result.generatedPassword}`);
      }
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create demo page");
    } finally {
      setIsCreating(false);
    }
  };

  const toggleActive = (id: string, isActive: boolean) => {
    dispatch(demoShareActions.updateById({ id, isActive: !isActive }));
  };

  const removeShare = (id: string) => {
    dispatch(demoShareActions.deleteById({ id }));
    setRegen((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const regeneratePassword = async (id: string) => {
    try {
      const resp = await makeRequest<{ data: { generatedPassword?: string } }>(`/v1/demo-shares/${id}/regenerate-password`, {
        method: "POST",
      });
      const pw = resp?.data?.generatedPassword;
      if (pw) setRegen((prev) => ({ ...prev, [id]: pw }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't regenerate password");
    }
  };

  const sortedShares = useMemo(() => [...shares].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()), [shares]);

  const copyBtn = (key: string, text: string, label = "Copy") => (
    <Button
      variant="info"
      size="sm"
      outline
      onClick={() => copy(key, text)}
    >
      <Icon
        name={copiedKey === key ? "check" : "copy"}
        size={14}
      />
      {copiedKey === key ? "Copied" : label}
    </Button>
  );

  // Low-profile, icon-only copy (used inline next to the link/password values).
  const iconCopy = (key: string, text: string, title = "Copy") => (
    <button
      type="button"
      title={copiedKey === key ? "Copied" : title}
      onClick={() => copy(key, text)}
      className="shrink-0 h-7 w-7 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <Icon
        name={copiedKey === key ? "check" : "copy"}
        size={15}
      />
    </button>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Share Demo"
      size="xl"
      className="!max-w-3xl"
      actions={
        <>
          <Button
            variant="neutral"
            size="sm"
            outline
            onClick={handleClose}
            disabled={isCreating}
          >
            Close
          </Button>
          <Button
            variant="success"
            size="sm"
            outline
            onClick={handleCreate}
            disabled={isCreating || !title.trim()}
          >
            {isCreating ? (
              <Icon
                name="loader"
                size={14}
                className="animate-spin"
              />
            ) : (
              <Icon
                name="share"
                size={14}
              />
            )}
            Create demo page
          </Button>
        </>
      }
    >
      <View className="p-5 space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Success panel — generated password + link, shown once on create */}
        {created && (
          <View className="rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/20 p-4 space-y-3">
            <View className="flex items-center gap-2">
              <Icon
                name="check-circle"
                size={16}
                className="text-emerald-500"
              />
              <Text className="text-sm font-semibold text-gray-900 dark:text-white">“{created.title}” is ready to share</Text>
            </View>
            <View className="space-y-2">
              <View className="flex items-center gap-2">
                <Text className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500 w-16 shrink-0">Link</Text>
                <Text className="text-xs font-mono text-gray-700 dark:text-gray-200 truncate flex-1">{publicUrlFor(created.slug)}</Text>
                {iconCopy(`link:${created.slug}`, publicUrlFor(created.slug), "Copy link")}
              </View>
              {created.password && (
                <View className="flex items-center gap-2">
                  <Text className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500 w-16 shrink-0">Password</Text>
                  <Text className="text-sm font-mono font-bold tracking-wider text-gray-900 dark:text-white flex-1">{created.password}</Text>
                  {iconCopy(`pw:${created.slug}`, created.password, "Copy password")}
                </View>
              )}
            </View>
            <View className="flex items-center gap-2 pt-1">
              {created.password && copyBtn("combo", `${publicUrlFor(created.slug)}\nPassword: ${created.password}`, "Copy link + password")}
              <a
                href={publicUrlFor(created.slug)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <Icon
                  name="open"
                  size={13}
                />
                Preview
              </a>
              <Button
                variant="neutral"
                size="sm"
                outline
                className="ml-auto"
                onClick={() => setCreated(null)}
              >
                Create another
              </Button>
            </View>
            <Text
              variant="muted"
              className="text-[11px]"
            >
              Save the password now — for security it isn’t shown again. You can generate a new one anytime below.
            </Text>
          </View>
        )}

        {/* Existing links */}
        {sortedShares.length > 0 && (
          <View className="space-y-2">
            <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Your demo links</Text>
            {sortedShares.map((share) => (
              <View
                key={share.id}
                className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 space-y-2"
              >
                <View className="flex items-center gap-3">
                  <View className="min-w-0 flex-1">
                    <View className="flex items-center gap-2">
                      <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">{share.title}</Text>
                      {share.hasPassword && (
                        <Icon
                          name="lock"
                          size={12}
                          className="text-gray-400 shrink-0"
                        />
                      )}
                      <Badge variant={share.isActive ? "success" : "default"}>{share.isActive ? "Active" : "Disabled"}</Badge>
                    </View>
                    <Text className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {publicUrlFor(share.slug)} · {share.viewCount} views
                    </Text>
                  </View>
                  {iconCopy(`link:${share.slug}`, publicUrlFor(share.slug), "Copy link")}
                  <MenuField
                    value=""
                    onChange={(v: string) => {
                      if (v === "regenerate") regeneratePassword(share.id);
                      else if (v === "toggle") toggleActive(share.id, share.isActive);
                      else if (v === "delete") removeShare(share.id);
                    }}
                    options={[
                      { label: "New password", value: "regenerate" },
                      { label: share.isActive ? "Disable" : "Enable", value: "toggle" },
                      { label: "Delete", value: "delete", textColor: "text-red-500" },
                    ]}
                  />
                </View>
                {regen[share.id] && (
                  <View className="flex items-center gap-2 rounded-md bg-gray-50 dark:bg-gray-800/50 px-2.5 py-1.5">
                    <Text className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500 shrink-0">New password</Text>
                    <Text className="text-sm font-mono font-bold tracking-wider text-gray-900 dark:text-white flex-1">{regen[share.id]}</Text>
                    {iconCopy(`pw:${share.id}`, regen[share.id], "Copy password")}
                  </View>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Create form */}
        <View className="space-y-4">
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Create a new demo page</Text>

          <View className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InputField
              label="Title"
              value={title}
              onChange={setTitle}
              placeholder="Acme Co · Voice AI demo"
            />
            <InputField
              label="Subtitle"
              value={subtitle}
              onChange={setSubtitle}
              placeholder="Booking assistant"
            />
          </View>

          <View>
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Intro</Text>
            <textarea
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={3}
              placeholder="A short intro shown in the page hero. Markdown supported."
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand/40"
            />
          </View>

          <View className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <View>
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Accent</Text>
              <View className="flex items-center gap-2 h-[38px]">
                {ACCENT_SWATCHES.map((swatch) => (
                  <button
                    key={swatch.hex}
                    type="button"
                    title={swatch.name}
                    onClick={() => setAccentColor(swatch.hex)}
                    className={`h-7 w-7 rounded-full border-2 transition-transform ${
                      accentColor === swatch.hex ? "border-gray-900 dark:border-white scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: swatch.hex }}
                  />
                ))}
              </View>
            </View>
            <View className="flex items-end">
              <View className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400 pb-1">
                <Icon
                  name="lock"
                  size={14}
                  className="text-gray-400 mt-0.5 shrink-0"
                />
                <Text
                  variant="muted"
                  className="text-xs"
                >
                  A secure password is generated automatically and shown once you create the page.
                </Text>
              </View>
            </View>
          </View>

          {/* Highlights */}
          <View className="space-y-2">
            <View className="flex items-center justify-between">
              <View>
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Highlights (stat cards)</Text>
                <Text
                  variant="muted"
                  className="text-xs block"
                >
                  Default stats — edit the numbers or remove any.
                </Text>
              </View>
              {highlights.length < MAX_HIGHLIGHTS && (
                <Button
                  variant="neutral"
                  size="sm"
                  outline
                  onClick={() => setHighlights((prev) => [...prev, { label: "", value: "" }])}
                >
                  <Icon
                    name="add"
                    size={14}
                  />
                  Add
                </Button>
              )}
            </View>
            {highlights.map((h, i) => (
              <View
                key={i}
                className="flex items-center gap-2"
              >
                <InputField
                  className="flex-1"
                  value={h.value}
                  onChange={(v: string) => updateHighlight(i, { value: v })}
                  placeholder="98%"
                />
                <InputField
                  className="flex-1"
                  value={h.label}
                  onChange={(v: string) => updateHighlight(i, { label: v })}
                  placeholder="Containment rate"
                />
                <Button
                  variant="danger"
                  size="sm"
                  outline
                  onClick={() => setHighlights((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <Icon
                    name="close"
                    size={14}
                  />
                </Button>
              </View>
            ))}
          </View>

          {/* Session picker */}
          <View className="space-y-2">
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Featured recordings {selectedSessionIds.length > 0 && `(${selectedSessionIds.length} selected)`}
            </Text>
            {sessions.length === 0 ? (
              <Text
                variant="muted"
                className="text-sm"
              >
                No sessions for this agent yet. Run a simulation or a call to capture recordings.
              </Text>
            ) : (
              <View className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                {sessions.slice(0, 60).map((s) => {
                  const selected = selectedSessionIds.includes(s.id);
                  return (
                    <View
                      key={s.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${selected ? "bg-brand/5" : "hover:bg-gray-50 dark:hover:bg-gray-800/50"}`}
                      onClick={() => toggleSession(s.id)}
                    >
                      <View
                        className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                          selected ? "bg-brand border-brand" : "border-gray-300 dark:border-gray-600"
                        }`}
                      >
                        {selected && (
                          <Icon
                            name="check"
                            size={12}
                            className="text-white"
                          />
                        )}
                      </View>
                      <Badge variant={s.origin === "simulation" ? "info" : "default"}>{s.origin || "session"}</Badge>
                      <Text className="text-xs text-gray-500 dark:text-gray-400">{s.status}</Text>
                      <Text className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{new Date(s.createdAt as any).toLocaleString()}</Text>
                    </View>
                  );
                })}
              </View>
            )}
          </View>

          {/* Live test toggles */}
          <View className="space-y-2">
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Let visitors test the agent</Text>
            {toggleRow("Talk to the agent in the browser", "A live mic call to this agent, right on the page.", webCallEnabled, setWebCallEnabled)}
            {toggleRow("Have the agent call their phone", "Requires outbound calling configured for this agent.", phoneCallEnabled, setPhoneCallEnabled)}
          </View>

          {/* CTAs */}
          <View className="space-y-2">
            <View className="flex items-center justify-between">
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Call-to-action buttons (optional)</Text>
              {ctas.length < 4 && (
                <Button
                  variant="neutral"
                  size="sm"
                  outline
                  onClick={() => setCtas((prev) => [...prev, { label: "", url: "" }])}
                >
                  <Icon
                    name="add"
                    size={14}
                  />
                  Add
                </Button>
              )}
            </View>
            {ctas.map((c, i) => (
              <View
                key={i}
                className="flex items-center gap-2"
              >
                <InputField
                  className="flex-1"
                  value={c.label}
                  onChange={(v: string) => updateCta(i, { label: v })}
                  placeholder="Book a call"
                />
                <InputField
                  className="flex-1"
                  value={c.url}
                  onChange={(v: string) => updateCta(i, { url: v })}
                  placeholder="https://cal.com/your-team"
                />
                <Button
                  variant="danger"
                  size="sm"
                  outline
                  onClick={() => setCtas((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <Icon
                    name="close"
                    size={14}
                  />
                </Button>
              </View>
            ))}
          </View>

          {error && <Text className="text-sm text-red-600 dark:text-red-400">{error}</Text>}
        </View>
      </View>
    </Dialog>
  );
};
